import { type GasPurePayload } from '@atlaskit/analytics-gas-types';

import { type FireOperationalEvent } from '../../common/types/new-avp-experience/analytics';

type HttpRequestMethod = 'GET' | 'POST' | 'PUT'; // extend as needed

const BASE_REQUEST_HEADERS = {
	'X-Request-Source': 'jsm-reporting-frontend',
	'Content-Type': 'application/json',
} as const;

interface AnalyticsOptions {
	analyticsPayload: Omit<GasPurePayload, 'action'>;
	fireOperationalEvent: FireOperationalEvent;
}

interface RequestOptions extends RequestInit {
	maxRetries?: number;
	retryDelayMs?: number;
}

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

export class FetchError extends Error {
	status: number;
	payload: unknown;

	constructor(message: string, status: number, payload: unknown) {
		super(message);
		this.status = status;
		this.payload = payload;
	}
}

const performRequest = async <T>(
	url: string,
	method: HttpRequestMethod,
	options: RequestOptions = {},
): Promise<T> => {
	const { maxRetries = 1, retryDelayMs = 1000, ...requestOptions } = options;

	const requestConfig: RequestInit = {
		method,
		...requestOptions,
		headers: {
			...BASE_REQUEST_HEADERS,
			...requestOptions.headers,
		},
	};

	let attempt = 1;

	while (attempt <= maxRetries) {
		try {
			const response = await fetch(url, requestConfig);

			// Handle non-ok responses
			if (!response.ok) {
				if (attempt < maxRetries) {
					await sleep(retryDelayMs);
					attempt++;
					continue;
				}

				const payload = await response.json().catch(() => response.text());

				throw new FetchError('[performRequest] Request failed', response.status, payload);
			}

			// Success - parse and return
			return response.json() as Promise<T>;
		} catch (error) {
			// Handle fetch errors (network issues, CORS, etc.)
			if (attempt < maxRetries) {
				await sleep(retryDelayMs);
				attempt++;
				continue;
			}
			throw error; // Re-throw the original error after all retries exhausted
		}
	}

	// This should never be reached in practice since the while loop will always either:
	// 1. Return successfully (response.ok === true)
	// 2. Throw an error (after all retries exhausted)
	// However, TypeScript's static analysis cannot prove this, so we need this fallback
	throw new Error('Unexpected end of retry loop');
};

const withAnalytics = async <T>(
	requestFn: () => Promise<T>,
	analyticsOptions?: AnalyticsOptions,
): Promise<T> => {
	try {
		const result = await requestFn();

		analyticsOptions?.fireOperationalEvent({
			action: 'succeeded',
			...analyticsOptions.analyticsPayload,
		});

		return result;
	} catch (error) {
		analyticsOptions?.fireOperationalEvent({
			action: 'failed',
			...analyticsOptions.analyticsPayload,
		});
		throw error;
	}
};

export const performReportsRequest = async <T>(
	url: string,
	method: HttpRequestMethod,
	body?: unknown,
	options?: RequestOptions,
	analyticsOptions?: AnalyticsOptions,
): Promise<T> => {
	const requestOptions: RequestOptions = {
		...options,
	};

	if (body) {
		requestOptions.body = JSON.stringify(body);
	}

	return withAnalytics(() => performRequest<T>(url, method, requestOptions), analyticsOptions);
};

// Convenience methods for backward compatibility
export const performReportsGetRequest = async <T>(
	url: string,
	options?: RequestOptions,
	analyticsOptions?: AnalyticsOptions,
) => performReportsRequest<T>(url, 'GET', undefined, options, analyticsOptions);

export const performReportsPostRequest = async <T>(
	url: string,
	body: unknown,
	options?: RequestOptions,
	analyticsOptions?: AnalyticsOptions,
) => performReportsRequest<T>(url, 'POST', body, options, analyticsOptions);

export const performReportsPutRequest = async <T>(
	url: string,
	body: unknown,
	options?: RequestOptions,
	analyticsOptions?: AnalyticsOptions,
) => performReportsRequest<T>(url, 'PUT', body, options, analyticsOptions);
